import { NgZone } from '@angular/core';

interface IonContentEl extends HTMLElement {
  getScrollElement?: () => Promise<HTMLElement>;
}

interface SpaceBelow {
  inFlow: number;
  fixedOverlap: number;
}

/** Never size the table below this, however little room the page leaves it. */
const MIN_FILL_PX = 120;

/**
 * Measures the height a table needs to fill the rest of its page — `scrollHeight="fill"`.
 *
 * A fixed guess like `calc(100vh - 7.5rem)` is wrong wherever the chrome above the table
 * isn't exactly 7.5rem: on an iPhone `100vh` spans the notch and home-indicator areas and
 * the header is taller, so the table's viewport ran past the bottom of the screen (the
 * last rows couldn't be reached) and left the page itself something to scroll (dragging
 * it slid the first rows under the sticky toolbar). Measuring instead:
 *
 *  - top: the table's own margin-box top, independent of how far the page is scrolled;
 *  - bottom: the bottom of the enclosing `ion-content` (or the window), less whatever
 *    must stay visible below the table — content laid out after it (customers'
 *    pagination), a bar fixed over the bottom edge (the waitlist footer), or else the
 *    home-indicator safe area.
 *
 * It re-measures when the page, the table's ancestors or their siblings change size, so a
 * toolbar that grows (an opened search, a wrapped row, a rotation) hands the table less
 * room straight away. A hidden page (Ionic keeps visited pages alive) is skipped until it
 * is shown again.
 */
export class TrFillHeight {
  private observer?: ResizeObserver;
  /** Siblings come and go too (the footer renders once settings load): re-observe then. */
  private mutations?: MutationObserver;
  private frame = 0;
  private scrollEl?: HTMLElement;
  private safeAreaProbe?: HTMLElement;
  private last = 0;

  constructor(
    private readonly host: HTMLElement,
    private readonly zone: NgZone,
    private readonly onChange: (heightPx: number) => void,
  ) {}

  start(): void {
    this.zone.runOutsideAngular(() => {
      const content = this.content();
      this.observer = new ResizeObserver(() => {
        this.schedule();
      });
      this.mutations = new MutationObserver(() => {
        this.observeLayout(content);
        this.schedule();
      });
      this.observeLayout(content);
      void content?.getScrollElement?.().then((scrollEl) => {
        this.scrollEl = scrollEl;
        this.schedule();
      });
      this.schedule();
    });
  }

  stop(): void {
    this.observer?.disconnect();
    this.observer = undefined;
    this.mutations?.disconnect();
    this.mutations = undefined;
    if (this.frame) {
      cancelAnimationFrame(this.frame);
      this.frame = 0;
    }
    this.safeAreaProbe?.remove();
    this.safeAreaProbe = undefined;
  }

  private content(): IonContentEl | null {
    return this.host.closest<IonContentEl>('ion-content');
  }

  /**
   * The page, and every ancestor of the table with its siblings, up to the page.
   * Observing an element twice is a no-op, so this is safe to re-run on mutation.
   */
  private observeLayout(content: HTMLElement | null): void {
    if (content) {
      this.observer?.observe(content);
    }
    let el: HTMLElement | null = this.host;
    while (el && el !== content) {
      const parentEl: HTMLElement | null = el.parentElement;
      if (parentEl) {
        this.mutations?.observe(parentEl, { childList: true });
        Array.from(parentEl.children).forEach((sibling) => {
          this.observer?.observe(sibling);
        });
      }
      el = parentEl;
    }
  }

  private schedule(): void {
    if (this.frame) {
      return;
    }
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      this.measure();
    });
  }

  private measure(): void {
    const content = this.content();
    const bounds = content?.getBoundingClientRect();
    if (bounds?.height === 0) {
      return; // hidden page
    }
    const bottom = bounds ? bounds.bottom : window.innerHeight;

    const hostRect = this.host.getBoundingClientRect();
    const tableTop =
      hostRect.top -
      parseFloat(getComputedStyle(this.host).marginTop) +
      (this.scrollEl?.scrollTop ?? 0);

    const { inFlow, fixedOverlap } = this.spaceBelow(content, bottom);
    // A bar fixed over the bottom edge already sits in the safe area, so the two
    // don't add up — whichever is taller is what the table must stay clear of.
    const reserved = inFlow + Math.max(fixedOverlap, this.safeAreaBottom());
    const height = Math.max(MIN_FILL_PX, Math.floor(bottom - tableTop - reserved));

    if (height !== this.last) {
      this.last = height;
      this.zone.run(() => {
        this.onChange(height);
      });
    }
  }

  /**
   * Room taken below the table, walking out from it to the page: siblings laid out
   * after each ancestor plus that ancestor's bottom padding/border (`inFlow`), and how
   * far any fixed sibling reaches up over the page's bottom edge (`fixedOverlap`).
   */
  private spaceBelow(content: HTMLElement | null, bottom: number): SpaceBelow {
    const space: SpaceBelow = {
      inFlow: 0,
      fixedOverlap: 0,
    };
    let el: HTMLElement | null = this.host;
    while (el && el !== content) {
      let next = el.nextElementSibling;
      while (next) {
        this.addSibling(space, next, bottom);
        next = next.nextElementSibling;
      }
      const parentEl: HTMLElement | null = el.parentElement;
      if (parentEl && parentEl !== content) {
        const style = getComputedStyle(parentEl);
        space.inFlow += parseFloat(style.paddingBottom) + parseFloat(style.borderBottomWidth);
      }
      el = parentEl;
    }

    return space;
  }

  /** Count one sibling after the table: in flow, fixed over the bottom edge, or neither. */
  private addSibling(space: SpaceBelow, sibling: Element, bottom: number): void {
    const style = getComputedStyle(sibling);
    if (style.display === 'none' || style.position === 'absolute') {
      return;
    }
    const rect = sibling.getBoundingClientRect();
    if (style.position === 'fixed') {
      space.fixedOverlap = Math.max(space.fixedOverlap, bottom - rect.top);
    } else {
      space.inFlow += rect.height + parseFloat(style.marginTop) + parseFloat(style.marginBottom);
    }
  }

  /** `env(safe-area-inset-bottom)` in px (the home indicator), via a hidden probe. */
  private safeAreaBottom(): number {
    if (!this.safeAreaProbe) {
      this.safeAreaProbe = document.createElement('div');
      this.safeAreaProbe.setAttribute('aria-hidden', 'true');
      this.safeAreaProbe.style.cssText =
        'position:fixed;bottom:0;left:0;width:0;visibility:hidden;pointer-events:none;' +
        'height:env(safe-area-inset-bottom, 0px);';
      document.body.appendChild(this.safeAreaProbe);
    }

    return this.safeAreaProbe.offsetHeight;
  }
}
